// Global search dropdown for the App Shell header (T-032, plan §5,
// spec §5.2 / §5.3).
//
// Keyboard- and mouse-driven; debounces input by 200 ms before
// firing GET /api/search. Renders results in two sections (Employees
// + Pages). Click or Enter navigates to the result; Esc clears.

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { api } from '../api/client';
import type { GlobalSearchResponse, GlobalSearchResult } from '@pa/shared';

const DEBOUNCE_MS = 200;

export function GlobalSearch() {
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce the input so we don't hammer /api/search on every
  // keystroke. 200 ms balances "feels live" against backend cost.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(rawQuery.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawQuery]);

  // Spec §5.3: dropdown renders when query is ≥2 characters.
  // Single-letter queries return too many false-positives at the
  // org scale we target.
  const QUERY_MIN_LENGTH = 2;
  const queryActive = debouncedQuery.length >= QUERY_MIN_LENGTH;

  const query = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: () =>
      api.get<GlobalSearchResponse>(
        `/api/search?q=${encodeURIComponent(debouncedQuery)}&limit=10`,
      ),
    enabled: queryActive,
    staleTime: 5_000,
  });

  // Flatten the two groups into a single navigable list for keyboard
  // arrow handling. The render layer keeps them visually grouped.
  const flatResults: GlobalSearchResult[] = query.data
    ? [...query.data.employees, ...query.data.pages]
    : [];

  // Reset highlight whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery, query.dataUpdatedAt]);

  // Close on click outside.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function pickResult(r: GlobalSearchResult) {
    if (r.kind === 'employee') {
      navigate(`/employees/${r.id}`);
    } else {
      navigate(r.href);
    }
    setRawQuery('');
    setDebouncedQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setRawQuery('');
      setDebouncedQuery('');
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, flatResults.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      const r = flatResults[activeIndex];
      if (r) {
        e.preventDefault();
        pickResult(r);
      }
      return;
    }
  }

  // Show the dropdown whenever the query is active AND has resolved
  // (success / error / loading) — including the empty-results case,
  // so the user sees "No results." instead of the dropdown
  // vanishing.
  const showDropdown =
    open &&
    queryActive &&
    (query.isLoading || query.isError || query.data !== undefined);

  // Spec §5.2: input renders inside a purple header — white text on
  // bg-white/10 with placeholder:text-white/50. Focus ring 2 px white/20.
  // Spec §5.3: dropdown is rounded-2xl shadow-xl with slate-100 border.
  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-md"
      data-testid="global-search"
    >
      <div className="relative">
        <Search
          size={18}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60"
        />
        <input
          ref={inputRef}
          type="search"
          value={rawQuery}
          placeholder="Search for people or areas..."
          onChange={(e) => {
            setRawQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="w-full pl-12 pr-10 py-2.5 text-sm bg-white/10 text-white placeholder:text-white/50 border-none rounded-2xl focus:ring-2 focus:ring-white/20 outline-none"
          data-testid="global-search-input"
        />
        {rawQuery.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setRawQuery('');
              setDebouncedQuery('');
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/60 hover:text-white"
            aria-label="Clear search"
            data-testid="global-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 max-h-[480px] overflow-hidden overflow-y-auto z-50 text-slate-800"
          data-testid="global-search-dropdown"
        >
          {query.isLoading && (
            <div className="px-4 py-3 text-xs text-slate-400">Searching…</div>
          )}
          {query.isError && (
            <div
              className="px-4 py-3 text-xs text-rose-600"
              data-testid="global-search-error"
            >
              Search failed.
            </div>
          )}
          {query.data && flatResults.length === 0 && !query.isLoading && (
            <div className="px-4 py-3 text-xs text-slate-400">No results.</div>
          )}
          {query.data && query.data.employees.length > 0 && (
            <ResultGroup
              label="People"
              results={query.data.employees}
              startIndex={0}
              activeIndex={activeIndex}
              onPick={pickResult}
            />
          )}
          {query.data && query.data.pages.length > 0 && (
            <ResultGroup
              label="Pages"
              results={query.data.pages}
              startIndex={query.data.employees.length}
              activeIndex={activeIndex}
              onPick={pickResult}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({
  label,
  results,
  startIndex,
  activeIndex,
  onPick,
}: {
  label: string;
  results: GlobalSearchResult[];
  startIndex: number;
  activeIndex: number;
  onPick: (r: GlobalSearchResult) => void;
}) {
  return (
    <div className="py-1">
      <div
        className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400"
        data-testid={`global-search-group-${label.toLowerCase()}`}
      >
        {label}
      </div>
      <ul>
        {results.map((r, i) => {
          const idx = startIndex + i;
          const isActive = idx === activeIndex;
          return (
            <li key={r.kind === 'employee' ? r.id : r.key}>
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur from firing before navigation.
                  e.preventDefault();
                  onPick(r);
                }}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 ${
                  isActive ? 'bg-slate-100' : 'hover:bg-slate-50'
                }`}
                data-testid={`global-search-result-${
                  r.kind === 'employee' ? r.id : r.key
                }`}
              >
                {r.kind === 'employee' ? (
                  <>
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {r.name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {[r.title, r.teamName, r.siteName]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </>
                ) : (
                  <span className="text-slate-700">{r.label}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
